import React, { useRef, useState, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

const tooltipRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

if (!tooltipRoot) {
  console.warn("Element with ID 'dropdown-root' not found. Ensure it's in your public/index.html for React Portals.");
}

const calculateTooltipPosition = (
  target: HTMLElement,
  tooltip: HTMLElement
): React.CSSProperties => {
  const targetRect = target.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  const spaceBelow = viewportHeight - targetRect.bottom;
  const spaceAbove = targetRect.top;

  const shouldDropUp = targetRect.bottom + tooltipRect.height > viewportHeight && spaceAbove > tooltipRect.height;

  const maxHeight = shouldDropUp ? spaceAbove - 10 : spaceBelow - 10;
  const top = shouldDropUp
    ? scrollY + targetRect.top - Math.min(tooltipRect.height, maxHeight)
    : scrollY + targetRect.bottom + 4;

  const left = scrollX + targetRect.left;

  return {
    position: 'absolute',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 10000,
    background: 'white',
    padding: '6px 10px',
    borderRadius: '6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    fontSize: '12px',
    maxWidth: '300px',
    minWidth: `${targetRect.width}px`,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto',
  };
};

interface TooltipPortalProps {
  targetRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  children: React.ReactNode;
}

const TooltipPortal: React.FC<TooltipPortalProps> = ({ targetRef, isOpen, children }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const prevTitleRef = useRef<string | null>(null);

  // Remove native title attribute when tooltip is open, restore when closed
  React.useEffect(() => {
    const target = targetRef.current;
    if (!target) {
      // Always return a cleanup function
      return () => {};
    }

    if (isOpen) {
      // Save previous title
      prevTitleRef.current = target.getAttribute('title');
      // Remove title to prevent native tooltip
      target.removeAttribute('title');
    } else {
      // Restore previous title if it existed
      if (prevTitleRef.current !== null) {
        target.setAttribute('title', prevTitleRef.current);
        prevTitleRef.current = null;
      }
    }
    // On unmount, restore title
    return () => {
      if (target && prevTitleRef.current !== null) {
        target.setAttribute('title', prevTitleRef.current);
        prevTitleRef.current = null;
      }
    };
  }, [isOpen, targetRef]);

  useLayoutEffect(() => {
    if (!isOpen || !targetRef.current || !tooltipRef.current) return;

    const newStyle = calculateTooltipPosition(targetRef.current, tooltipRef.current);
    setStyle(newStyle);
  }, [isOpen, targetRef]);

  if (!isOpen || !tooltipRoot) return null;

  return ReactDOM.createPortal(
    <div ref={tooltipRef} style={style}>
      {children}
    </div>,
    tooltipRoot
  );
};

export default TooltipPortal;
